import {
    addDoc,
    AggregateField,
    AggregateQuerySnapshot,
    arrayRemove,
    average,
    collection,
    CollectionReference,
    deleteDoc,
    deleteField,
    doc,
    DocumentData,
    DocumentReference,
    DocumentSnapshot,
    FieldValue,
    getAggregateFromServer,
    getCountFromServer,
    getDoc,
    getDocs,
    onSnapshot,
    or,
    Query,
    query,
    QuerySnapshot,
    serverTimestamp,
    setDoc,
    SnapshotOptions,
    Timestamp,
    updateDoc,
    where,
    writeBatch,
} from "firebase/firestore";
import { firestore } from "./FirebaseConfig";
import { IClass, LeaderboardStudent, UserHistory } from "./Types";
// import { GetScenario, Scenario, scenarios } from "./Scenarios/Scenarios";

const db = firestore;

// const gameGroupsKey: string = "gameGroupsDev";
// const roleKey: string = "role";
const studentKey: string = "student";
const tasksKey: string = "tasks";
const cohortIdKey: string = "cohortId";

const gameGroupsKey = () => {
    switch (process.env.NODE_ENV) {
        case "development":
            return "_DEV_gameRooms";
        case "test":
            return "_DEV_gameRooms";
        case "production":
            return "gameRooms";
        default:
            return "gameRooms";
    }
};

const classesKey = () => {
    switch (process.env.NODE_ENV) {
        case "development":
            return "_DEV_classes";
        case "test":
            return "_DEV_classes";
        case "production":
            return "classes";
        default:
            return "classes";
    }
};

const groupHistoryKey = () => {
    switch (process.env.NODE_ENV) {
        case "development":
            return "_DEV_roomHistory";
        case "test":
            return "_DEV_roomHistory";
        case "production":
            return "roomHistory";
        default:
            return "roomHistory";
    }
};

const userHistoryKey = () => {
    switch (process.env.NODE_ENV) {
        case "development":
            return "_DEV_userHistory";
        case "test":
            return "_DEV_userHistory";
        case "production":
            return "userHistory";
        default:
            return "userHistory";
    }
};

const cohortsKey = () => {
    switch (process.env.NODE_ENV) {
        case "development":
            return "_DEV_cohorts";
        case "test":
            return "_DEV_cohorts";
        case "production":
            return "cohorts";
        default:
            return "cohorts";
    }
};

const usersKey = () => {
    switch (process.env.NODE_ENV) {
        case "development":
            return "_DEV_users";
        case "test":
            return "_DEV_users";
        case "production":
            return "users";
        default:
            return "users";
    }
};

// ---------- Groups ----------

class Group {
    isPlaying: boolean;

    constructor(isPlaying: boolean) {
        this.isPlaying = isPlaying;
    }
    toString() {
        return "";
    }
}

// Firestore data converter
const groupConverter = {
    toFirestore: (group: Group) => {
        return {
            isPlaying: group.isPlaying,
        };
    },
    fromFirestore: (snapshot: DocumentSnapshot, options: SnapshotOptions) => {
        const data = snapshot.data(options);
        return new Group(data?.isPlaying);
    },
};

function GenerateGroupCode() {
    var code = "";
    var digits = "0123456789";
    var length = 6;
    for (var i = 0; i < length; i++) {
        code += digits.charAt(Math.floor(Math.random() * digits.length));
    }

    return code;
}

async function CreateGroup(scenIndex: number): Promise<DocumentReference> {
    var groupCode: string = GenerateGroupCode();
    const docRef = doc(db, gameGroupsKey(), groupCode);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        return Promise.reject("Group Code is not unique. Please try again.");
    } else {
        setDoc(docRef, {
            dateTimeCreated: serverTimestamp(),
            hostId: "",
            classId: "",
            groupNumber: "",
            hostName: "",
            isPlaying: false,
            assignedPlayers: [],
            players: {},
            playersList: [],
            scenarioIndex: scenIndex,
        });

        return docRef;
    }
}

async function CreateGroupInClass(classId: string, scenIndex: number, groupNumber: number, assignedPlayers: string[]) {
    addDoc(collection(db, gameGroupsKey()), {
        dateTimeCreated: serverTimestamp(),
        hostId: "",
        classId: classId,
        groupNumber: groupNumber,
        hostName: "",
        isPlaying: false,
        assignedPlayers: assignedPlayers,
        players: {},
        playersList: [],
        scenarioIndex: scenIndex,
    });
}

interface UploadGroup {
    number: number;
    studentIds: string[];
}

async function CreateGroupsInClass(classId: string, scenIndex: number, uploadGroups: UploadGroup[]) {
    uploadGroups.forEach(group => {
        addDoc(collection(db, gameGroupsKey()), {
            dateTimeCreated: serverTimestamp(),
            hostId: "",
            classId: classId,
            groupNumber: group.number,
            hostName: "",
            isPlaying: false,
            assignedPlayers: group.studentIds,
            players: {},
            playersList: [],
            scenarioIndex: scenIndex,
        });
    });
}

async function EditGroupInClass(groupCode: string, groupNumber: number, assignedPlayers: string[]) {
    const docRef = doc(db, gameGroupsKey(), groupCode);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        await updateDoc(docRef, {
            groupNumber: groupNumber,
            assignedPlayers: assignedPlayers,
        });
    } else {
        return Promise.reject("Could not find group. Please refresh and try again.");
    }
}

function CloseGroup(groupId: string): Promise<void> {
    return deleteDoc(doc(db, gameGroupsKey(), groupId));
}

async function ResetGroup(groupId: string): Promise<void> {
    const tasksSnap = await getDocs(collection(db, gameGroupsKey(), groupId, tasksKey));
    tasksSnap.forEach((taskDoc) => {
        deleteDoc(doc(db, gameGroupsKey(), groupId, tasksKey, taskDoc.id));
    });

    const groupRef = doc(db, gameGroupsKey(), groupId);
    updateDoc(groupRef, {
        dateTimeStarted: serverTimestamp(),
        hostId: "",
        hostName: "",
        isPlaying: false,
        players: {},
        playersList: []
    });
}

async function RemovePlayerFromGroup(groupId: string, playerId: string) {
    const groupRef = doc(db, gameGroupsKey(), groupId);
    updateDoc(groupRef, {
        [`players.${playerId}`]: deleteField(),
        playersList: arrayRemove(playerId)
    });

    const tasksSnap = await getDocs(collection(db, gameGroupsKey(), groupId, tasksKey));
    tasksSnap.forEach((taskDoc) => {
        const taskRef = doc(db, gameGroupsKey(), groupId, tasksKey, taskDoc.id);
        updateDoc(taskRef, {
            [`answers.${playerId}`]: deleteField()
        })
    });
}

function GetAllGroupsCount(): Promise<
    AggregateQuerySnapshot<
        {
            count: AggregateField<number>;
        },
        DocumentData,
        DocumentData
    >
> {
    return getCountFromServer(collection(db, gameGroupsKey()));
}

function GetAllGroups(): Promise<QuerySnapshot<DocumentData, DocumentData>> {
    return getDocs(collection(db, gameGroupsKey()));
}

function GetAllGroupsInClass(classId: string): Query<DocumentData, DocumentData> {
    return query(collection(db, gameGroupsKey()), where("classId", "==", classId));
}

function GetGroup(groupId: string): Promise<DocumentSnapshot> {
    return getDoc(doc(db, gameGroupsKey(), groupId));
}

function GetGroupRef(groupId: string): DocumentReference<DocumentData, DocumentData> {
    return doc(db, gameGroupsKey(), groupId);
}

// function StartGroup(groupId: string) {
// 	const groupRef = doc(db, gameGroupsKey(), groupId);
// 	updateDoc(groupRef, {
// 		isPlaying: true
// 	});

// 	Scenarios.forEach((task) => {
// 		setDoc(doc(db, `${gameGroupsKey}/${groupId}/tasks`, task.id), {
// 			answers: {},
// 			answersInitial: {},
// 			chat: [],
// 			hasShared: [],
// 			isSolo: task.isSolo,
// 			readyToAnswer: [],
// 			readyToDiscuss: [],
// 			roleIndexes: task.roleIndexes,
// 			roleIndexesAnswerStart: task.roleIndexesAnswerStart,
// 			stageIndex: task.stageIndex,
// 			taskIndex: task.taskIndex
// 		})
// 	});
// }

function GetTasksInGroup(groupId: string): CollectionReference<DocumentData, DocumentData> {
    return collection(db, gameGroupsKey(), groupId, tasksKey);
}

// ---------- Classes ----------

async function CreateClass(code: string, userId: string, session: number, scenarioIndex: number, startTime: Timestamp, endTime: Timestamp): Promise<DocumentReference> {
    return addDoc(collection(db, classesKey()), {
        code: code,
        session: session,
        scenarioIndex: scenarioIndex,
        startTime: startTime,
        endTime: endTime,
        dateTimeCreated: serverTimestamp(),
        ownerId: userId,
        editorIds: [],
        viewerIds: [],
        canEditByLink: false,
        canViewByLink: false
    });
}

async function EditClass(classId: string, code: string, session: number, scenIndex: number, startTime: Timestamp, endTime: Timestamp) {
    const classRef = doc(db, classesKey(), classId);
    const batch = writeBatch(db);

    batch.update(classRef, {
        code: code,
        session: session,
        scenarioIndex: scenIndex,
        startTime: startTime,
        endTime: endTime
    });

    const q = await query(collection(db, gameGroupsKey()), where("classId", "==", classId));
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((doc) => {
        batch.update(doc.ref, { scenarioIndex: scenIndex })
    });

    await batch.commit();
}

function GetClass(classId: string): Promise<DocumentSnapshot> {
    return getDoc(doc(db, classesKey(), classId));
}

async function HasAccessToClass(classId: string, userId: string) {
    const docSnap = await getDoc(doc(db, classesKey(), classId));

    if (docSnap.exists()) {
        const resClass: IClass = {
            id: docSnap.id,
            code: docSnap.data().code,
            session: docSnap.data().session,
            scenarioIndex: docSnap.data().scenarioIndex,
            startTime: docSnap.data().startTime,
            endTime: docSnap.data().endTime,
            dateTimeCreated: docSnap.data().dateTimeCreated,
            ownerId: docSnap.data().ownerId,
            editorIds: docSnap.data().editorIds,
            viewerIds: docSnap.data().viewerIds,
            canEditByLink: docSnap.data().canEditByLink,
            canViewByLink: docSnap.data().canViewByLink
        }

        if (resClass.canEditByLink == true ||
            resClass.canViewByLink == true ||
            resClass.editorIds.includes(userId) ||
            resClass.viewerIds.includes(userId) ||
            resClass.ownerId === userId) {
            return true;
        }

    } else {
        return false;
    }

    return false;
}

function GetClassRef(classId: string): DocumentReference<DocumentData, DocumentData> {
    return doc(db, classesKey(), classId);
}

function GetAllClasses(): Promise<QuerySnapshot<DocumentData, DocumentData>> {
    return getDocs(collection(db, classesKey()));
}

function GetAllClassesWithPermission(userId: string) {
    const classesRef = collection(db, classesKey());

    const q = query(classesRef,
        or(where('canViewByLink', '==', true),
            where('canEditByLink', '==', true),
            where("viewerIds", "array-contains", userId),
            where("editorIds", "array-contains", userId),
            where('ownerId', '==', userId)
        )
    );

    return getDocs(q);
}

async function DeleteClass(classId: string): Promise<void> {
    const batch = writeBatch(db);
    const classRef = doc(db, classesKey(), classId);

    batch.delete(classRef);

    const q = await query(collection(db, gameGroupsKey()), where("classId", "==", classId));
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
    });

    await batch.commit();
}

function SetClassAccess(classId: string, editorsValue: string[], viewersValue: string[], canEditByLinkValue: boolean, canViewByLinkValue: boolean) {
    const classRef = doc(db, classesKey(), classId);

    updateDoc(classRef, {
        editorIds: editorsValue,
        viewerIds: viewersValue,
        canEditByLink: canEditByLinkValue,
        canViewByLink: canViewByLinkValue
    });
}

// ---------- History ----------

function GetAllHistory(): Promise<QuerySnapshot<DocumentData, DocumentData>> {
    return getDocs(collection(db, groupHistoryKey()));
}

function GetHistoryDoc(historyId: string): Promise<DocumentSnapshot> {
    return getDoc(doc(db, groupHistoryKey(), historyId));
}


// ---------- Students ----------

function GetAllStudents(): Promise<QuerySnapshot<DocumentData, DocumentData>> {
    return getDocs(query(collection(db, usersKey()), where("role", "==", "student")));
}

function GetStudentHistory(studentId: string): Promise<QuerySnapshot<DocumentData, DocumentData>> {
    const userHistoryCollection = query(collection(db, userHistoryKey()), where("userId", "==", studentId));
    return getDocs(userHistoryCollection);
}

async function GetCohortHistory(cohortId: string): Promise<UserHistory[]> {
    let value: UserHistory[] = [];
    const cohortHistorySnapshot = await getDocs(query(collection(db, userHistoryKey()), where(cohortIdKey, "==", cohortId)));
    for (const historySnapshot of cohortHistorySnapshot.docs) {
        value.push({
            id: historySnapshot.id,
            scenarioIndex: historySnapshot.data().scenarioIndex,
            dateTimeCreated: historySnapshot.data().dateTimeCreated,
            overallTimeSec: historySnapshot.data().overallTimeSec,
            correctTasksCount: historySnapshot.data().correctTasksCount,
            allTasksCount: historySnapshot.data().allTasksCount
        });
    }
    return value;
}

function SetCohortInStudent(userId: string, cohortId: string) {
    const docRef = doc(db, usersKey(), userId);
    updateDoc(docRef, {
        [cohortIdKey]: cohortId,
    });
}

async function GetAllStudentsHistoryItems(cohortId: string = ""): Promise<LeaderboardStudent[]> {
    let docsQuery = null;
    let studentsHistory: LeaderboardStudent[] = [];

    if (cohortId === "" || cohortId === undefined) {
        docsQuery = getDocs(query(collection(db, usersKey()), where("role", "==", "student")));
    } else {
        docsQuery = getDocs(query(collection(db, usersKey()), where("role", "==", "student"), where(cohortIdKey, "==", cohortId)));
    }
    const studentsSnapshot = await docsQuery;

    for (const studentSnapshot of studentsSnapshot.docs) {
        let studentHistory: LeaderboardStudent = {
            userId: studentSnapshot.id,
            userName: studentSnapshot.data().fullName,
            cohortId: studentSnapshot.data().cohortId,
            averageCorrectPercentage: 0,
            averageOverallTimeSec: 0,
        };
        const userHistoryCollection = collection(db, usersKey(), studentSnapshot.id, userHistoryKey());
        const checkHistoryColl = await getDocs(userHistoryCollection);
        if (checkHistoryColl.size > 1) {
            const snapshot = await getAggregateFromServer(userHistoryCollection, {
                correctTasksAverage: average("correctTasksCount"),
                assignedTasksAverage: average("allTasksCount"),
                timeTakenSecAverage: average("overallTimeSec"),
            });

            let correctData = snapshot.data().correctTasksAverage;
            let assignedData = snapshot.data().assignedTasksAverage;
            let timeData = snapshot.data().timeTakenSecAverage;

            let correct: number = correctData !== null ? correctData : 0;
            let assigned: number = assignedData !== null ? assignedData : 0;
            let time: number = timeData !== null ? timeData : 0;

            studentHistory.averageCorrectPercentage = (correct / assigned) * 100;
            studentHistory.averageOverallTimeSec = time;
        }
        studentsHistory.push(studentHistory);
    }

    return studentsHistory;
}

async function CreateTeacher(email: string, password: string, name: string) {
    const baseUrl: string = "https://createuser-jwrwvreegq-uc.a.run.app"
    const requestUrl: string = `${baseUrl}?email=${email}&password=${password}&name=${name}&role=teacher`;

    try {
        const response = await fetch(requestUrl);
        if (!response.ok) throw new Error('Request failed'); // Check for 200-299 status
        const data = await response.json();
        return (data);
    }
    catch (err) {
        return (err);
    }
}


async function CreateStudent(email: string, password: string, name: string) {
    const baseUrl: string = "https://createuser-jwrwvreegq-uc.a.run.app"
    const requestUrl: string = `${baseUrl}?email=${email}&password=${password}&name=${name}&role=student`;

    try {
        const response = await fetch(requestUrl);
        if (!response.ok) throw new Error('Request failed'); // Check for 200-299 status
        const data = await response.json();
        return (data);
    }
    catch (err) {
        return (err);
    }
}

// ---------- Users ----------

function CreateTeacherUser(newId: string, newFullName: string, newEmail: string) {
    setDoc(doc(db, usersKey(), newId), {
        createdAt: serverTimestamp(),
        fullName: newFullName,
        email: newEmail,
        lastActive: new Date(),
        role: "teacher",
    });
}

function UpdateLastActiveInUser(userId: string): void {
    const docRef = doc(db, usersKey(), userId);
    updateDoc(docRef, {
        lastActive: new Date(),
    });
}

function GetUser(userId: string): Promise<DocumentSnapshot> {
    return getDoc(doc(db, usersKey(), userId));
}

function SetFullNameInUser(userId: string, newName: string): void {
    const docRef = doc(db, usersKey(), userId);
    updateDoc(docRef, {
        "fullName": newName,
    });
}

function GetAllUsers(): Promise<QuerySnapshot<DocumentData, DocumentData>> {
    return getDocs(collection(db, usersKey()));
}

function GetAllStaffUsers(): Promise<QuerySnapshot<DocumentData, DocumentData>> {
    const usersCol = collection(db, usersKey());
    const q = query(usersCol,
        or(where('role', '==', "teacher"),
            where('role', '==', "admin")
        )
    );
    return getDocs(q);
}

function GetAllManagedUsers(): Promise<QuerySnapshot<DocumentData, DocumentData>> {
    const usersCol = collection(db, "users");
    const q = query(usersCol, where('isManaged', '==', true));
    return getDocs(q);
}

function GetCohort(cohortId: string): Promise<DocumentSnapshot> {
    return getDoc(doc(db, cohortsKey(), cohortId));
}

function GetAllCohorts(): Promise<QuerySnapshot<DocumentData, DocumentData>> {
    return getDocs(collection(db, cohortsKey()));
}

function AddCohort(cohortName: string): Promise<DocumentReference<DocumentData, DocumentData>> {
    return addDoc(collection(db, cohortsKey()), {
        name: cohortName,
    });
}

async function DeleteCohort(cohortId: string) {
    const batch = writeBatch(db);

    const querySnapshot = await getDocs(query(collection(db, usersKey()), where(cohortIdKey, "==", cohortId)));
    querySnapshot.forEach((docSnap) => {
        const docRef = doc(db, usersKey(), docSnap.id);
        batch.update(docRef, { [cohortIdKey]: "" });
    });

    const cohortDoc = doc(db, cohortsKey(), cohortId)
    batch.delete(cohortDoc);

    await batch.commit();
}

async function DebugOnlyIndexBuilder() {
    // const groupsRef = collection(db, gameGroupsKey());
    // return await getDocs(query(groupsRef, where("playersList", "array-contains", "")))
}

async function DeleteUser(id: string) {
    const baseUrl: string = "https://deleteuser-jwrwvreegq-uc.a.run.app";
    const requestUrl: string = `${baseUrl}?uid=${id}`;

    try {
        const response = await fetch(requestUrl);
        if (!response.ok) throw new Error('Request failed'); // Check for 200-299 status
        const data = await response.json();
        return (data);
    }
    catch (err) {
        return (err);
    }
}

async function UpdateUser(id: string, email: string, name: string, password: string) {
    const baseUrl: string = "https://updateuser-jwrwvreegq-uc.a.run.app";
    const requestUrl: string = `${baseUrl}?uid=${id}&email=${email}&name=${name}&password=${password}`;

    try {
        const response = await fetch(requestUrl);
        if (!response.ok) throw new Error('Request failed'); // Check for 200-299 status
        const data = await response.json();
        return (data);
    }
    catch (err) {
        return (err);
    }
}

export {
    GetAllGroupsCount,
    GetAllGroups,
    GetGroup,
    GetGroupRef,
    GetAllStudents,
    CreateClass,
    EditClass,
    GetClass,
    HasAccessToClass,
    GetClassRef,
    GetAllClasses,
    GetAllClassesWithPermission,
    SetClassAccess,
    DeleteClass,
    CreateGroup,
    EditGroupInClass,
    CreateGroupInClass,
    CreateGroupsInClass,
    GetAllGroupsInClass,
    ResetGroup,
    CloseGroup,
    CreateTeacherUser,
    GetTasksInGroup,
    GetAllHistory,
    GetHistoryDoc,
    GetUser,
    GetAllUsers,
    GetAllStaffUsers,
    GetAllManagedUsers,
    GetAllCohorts,
    AddCohort,
    DeleteCohort,
    SetCohortInStudent,
    GetAllStudentsHistoryItems,
    UpdateLastActiveInUser,
    GetCohort,
    GetStudentHistory,
    GetCohortHistory,
    SetFullNameInUser,
    RemovePlayerFromGroup,
    CreateTeacher,
    CreateStudent,
    UpdateUser,
    DeleteUser,
    DebugOnlyIndexBuilder
};
